import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Client-side zoom for a Stock chart, replacing Highcharts' rangeSelector so
 * the buttons can be ordinary React UI. Presets zoom to the LAST N hours of
 * the loaded data; "all" clears the extremes. The active preset is DERIVED
 * from the axis extremes after every change (navigator drag, pan, shared
 * navigator propagation), so it can never drift from what is on screen.
 */
export const ZOOM_PRESETS = [
    { key: 'all', hours: null },
    { key: '1h', hours: 1 },
    { key: '3h', hours: 3 },
];

const TRIGGER = 'zoomControl';
const TOL = 90e3; // 1.5 min tolerance when matching a preset window

/** Which preset (if any) the extremes correspond to. */
export function zoomFromExtremes({ min, max, dataMin, dataMax }) {
    if (min === null || min === undefined || max === null || max === undefined) return 'all';
    if (dataMin === null || dataMin === undefined || dataMax === null || dataMax === undefined) return null;
    if (Math.abs(min - dataMin) <= TOL && Math.abs(max - dataMax) <= TOL) return 'all';
    if (Math.abs(max - dataMax) > TOL) return null;
    for (const p of ZOOM_PRESETS) {
        if (p.hours !== null && Math.abs((max - min) - p.hours * 3600e3) <= TOL) return p.key;
    }
    return null;
}

export function applyZoom(chart, hours) {
    const axis = chart && chart.xAxis && chart.xAxis[0];
    if (!axis) return;
    if (hours === null) {
        axis.setExtremes(null, null, true, false, { trigger: TRIGGER });
        return;
    }
    const max = axis.dataMax;
    const min = axis.dataMin;
    if (max === null || max === undefined) return;
    axis.setExtremes(Math.max(min, max - hours * 3600e3), max, true, false, { trigger: TRIGGER });
}

export function useZoomEvents(setZoomState) {
    return useMemo(() => ({
        afterSetExtremes(e) {
            if (!e || !Number.isFinite(e.dataMin) || !Number.isFinite(e.dataMax)) return; // empty chart: nothing to derive
            setZoomState(zoomFromExtremes({ min: e.userMin ?? e.min, max: e.userMax ?? e.max, dataMin: e.dataMin, dataMax: e.dataMax }));
        },
    }), [setZoomState]);
}

/**
 * @param resetKey  changes → back to "all" (a new range/station: a stale 1h window must not survive)
 * @param dataKey   changes → keep the user's preset but slide it to the newest data (background refresh)
 */
export default function useChartZoom(resetKey, dataKey) {
    const chartRef = useRef(null);
    const [zoom, setZoomState] = useState('all');
    const zoomRef = useRef(zoom);
    zoomRef.current = zoom;

    const setZoom = useCallback((key) => {
        const preset = ZOOM_PRESETS.find((z) => z.key === key);
        if (!preset) return;
        applyZoom(chartRef.current && chartRef.current.chart, preset.hours);
        setZoomState(key);
    }, []);

    // New range/station → back to "all" so a stale 1h window isn't kept.
    useEffect(() => { setZoom('all'); }, [resetKey, setZoom]);
    // Background refresh (same range, new points) → re-apply the current preset so it ends at the newest data.
    const lastReset = useRef(resetKey);
    useEffect(() => {
        if (lastReset.current !== resetKey) { lastReset.current = resetKey; return; } // the reset effect handles this render
        if (dataKey !== undefined && zoomRef.current !== 'all') setZoom(zoomRef.current);
    }, [dataKey, resetKey, setZoom]);

    const xAxisEvents = useZoomEvents(setZoomState);

    return { chartRef, zoom, setZoom, xAxisEvents };
}
